import React from 'react';
import {FileText, Loader2, Plus, Wand2, X} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {Button} from '@common-web/components/ui/button';
import {Card} from '@common-web/components/ui/card';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {useFissionMessage} from '@common-web/hooks/use-fission-message';
import {AccountProposalGenerator} from '../components/AccountProposalGenerator';
import type {ProposalGenerationType} from '../components/GenerateProposalModal';
import {ProposalRow} from '@common-web/components/proposals/ProposalRow';

interface ProposalsTabProps {
  accountsId: string;
  /** Optional controlled state for the generator dialog (lets other tabs open it). */
  generatorOpen?: boolean;
  onGeneratorOpenChange?: (open: boolean) => void;
  /**
   * Preset proposal type for the embedded generator (Quick Proposal vs Informed
   * Analysis) — carried in from the Generate-proposal modal via `?generate=`.
   */
  initialType?: ProposalGenerationType;
}

export function ProposalsTab({
  accountsId,
  generatorOpen: controlledGeneratorOpen,
  onGeneratorOpenChange,
  initialType,
}: ProposalsTabProps): React.ReactElement {
  const utils = trpc.useUtils();
  // Generator dialog state can be controlled by other tabs (e.g. Overview's
  // "Generate proposal" button) or managed internally here.
  const [internalGeneratorOpen, setInternalGeneratorOpen] = React.useState(false);
  const generatorOpen = controlledGeneratorOpen ?? internalGeneratorOpen;
  const setGeneratorOpen = React.useCallback(
    (open: boolean) => {
      setInternalGeneratorOpen(open);
      onGeneratorOpenChange?.(open);
    },
    [onGeneratorOpenChange],
  );
  // When set, an Informed Analysis report is shown inline (resolved to a
  // tokenized /report URL at view time via getIaReportUrl).
  const [reportProposalId, setReportProposalId] = React.useState<string | null>(null);
  // Id of the proposal pending a delete confirmation.
  const [pendingDeleteId, setPendingDeleteId] = React.useState<string | null>(null);

  const proposals = trpc.account.getProposals.useQuery({accountsId});
  // Already fetched/cached for this account; used to title proposals by company.
  const account = trpc.account.getById.useQuery({accountsId});
  const companyName = account.data?.companyName;
  // The embed origin is returned alongside the generator URL and is used to
  // verify postMessage events before persisting any proposal (cached/shared
  // with AccountProposalGenerator's identical query).
  const fissionUrl = trpc.account.getFissionUrl.useQuery(
    {accountsId},
    {enabled: !!accountsId, refetchOnWindowFocus: false},
  );

  const reportUrl = trpc.account.getIaReportUrl.useQuery(
    {proposalId: reportProposalId ?? ''},
    {enabled: reportProposalId !== null},
  );
  const del = trpc.account.deleteProposal.useMutation({
    onSuccess: () => {
      utils.account.getProposals.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
    },
  });

  const save = trpc.account.saveProposal.useMutation({
    onSuccess: () => {
      // Refresh the proposals list + activity feed, and the account itself so
      // the auto LEAD → PROPOSAL transition is reflected in the header, stage
      // progress, and Overview summary.
      utils.account.getProposals.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
      utils.account.getById.invalidate({accountsId});
      toast({
        title: 'Proposal saved',
        description: 'The proposal has been added to this account.',
      });
    },
    onError: createMutationErrorHandler('Failed to save proposal'),
  });

  // The Fission embed posts a message back to the parent window when a proposal
  // is produced. We persist a few kinds:
  //   - `proposal-saved`      → external Quick Proposal link (url + password)
  //   - `ia-run-created`      → Informed Analysis resource (resourceId)
  //   - `qp-proposal-created` → Infinity-hosted Quick Proposal resource
  //                              (resourceId; Synrgy — we mint link + code)
  // resourceId-backed proposals resolve to a report URL at view time. Messages
  // are only honored when they originate from the verified Fission embed origin.
  useFissionMessage(fissionUrl.data?.embedOrigin, {
    onProposalSaved: ({url, password, label}) =>
      save.mutate({
        accountsId,
        url,
        password: password || undefined,
        label: label || undefined,
      }),
    onIaRunCreated: ({resourceId, companyName}) =>
      save.mutate({
        accountsId,
        type: 'INFORMED_ANALYSIS',
        resourceId,
        label: companyName || undefined,
      }),
    onQpProposalCreated: ({resourceId, companyName}) =>
      save.mutate({
        accountsId,
        type: 'QUICK_PROPOSAL',
        resourceId,
        label: companyName || undefined,
      }),
  });

  return (
    <div className='space-y-5'>
      <Card className='border-border p-6 shadow-sm'>
        <div className='mb-5 flex items-start justify-between gap-4'>
          <div>
            <h3 className='text-sm font-semibold uppercase tracking-wide text-foreground'>
              Proposals
            </h3>
            <p className='mt-1 text-sm text-muted-foreground'>
              Generate and manage proposals for this account.
            </p>
          </div>
          <div className='flex items-center gap-3'>
            {generatorOpen && save.isPending ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                Saving proposal…
              </div>
            ) : null}
            <Button
              type='button'
              variant={generatorOpen ? 'ghost' : 'outline'}
              size='sm'
              onClick={() => setGeneratorOpen(!generatorOpen)}
            >
              {generatorOpen ? (
                <>
                  <X className='w-4 h-4 mr-1.5' />
                  Close generator
                </>
              ) : (
                <>
                  <Plus className='w-4 h-4 mr-1.5' />
                  Generate proposal
                </>
              )}
            </Button>
          </div>
        </div>

        {generatorOpen ? (
          <div className='h-[calc(100vh-18rem)] min-h-[600px] overflow-hidden rounded-lg border border-border/40 bg-muted/5'>
            <AccountProposalGenerator accountsId={accountsId} initialType={initialType} />
          </div>
        ) : reportProposalId ? (
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <h4 className='text-sm font-medium'>Informed analysis results</h4>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => setReportProposalId(null)}
              >
                <X className='w-4 h-4 mr-1.5' />
                Close
              </Button>
            </div>
            <div className='h-[calc(100vh-20rem)] min-h-[600px] overflow-hidden rounded-lg border border-border/40 bg-muted/5'>
              {reportUrl.isLoading ? (
                <div className='flex h-full items-center justify-center text-sm text-muted-foreground'>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Loading results…
                </div>
              ) : reportUrl.data?.url ? (
                <iframe
                  title='Informed analysis report'
                  src={reportUrl.data.url}
                  className='h-full w-full border-0'
                />
              ) : (
                <div className='flex h-full items-center justify-center text-sm text-muted-foreground'>
                  Unable to load results.
                </div>
              )}
            </div>
          </div>
        ) : proposals.isLoading ? (
          <div className='text-sm text-muted-foreground'>Loading proposals…</div>
        ) : proposals.data && proposals.data.length > 0 ? (
          <div className='space-y-2'>
            {proposals.data.map((p) => (
              <ProposalRow
                key={p.id}
                proposal={p}
                companyName={companyName}
                onView={setReportProposalId}
                onDelete={setPendingDeleteId}
              />
            ))}
          </div>
        ) : (
          <div className='flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-10 text-center'>
            <FileText className='h-6 w-6 text-muted-foreground' />
            <p className='text-sm font-medium text-foreground'>No proposals yet.</p>
            <p className='text-xs text-muted-foreground'>
              Use “Generate proposal” to create the first one.
            </p>
            <Button type='button' size='sm' className='mt-2' onClick={() => setGeneratorOpen(true)}>
              <Wand2 className='w-4 h-4 mr-1.5' />
              Generate proposal
            </Button>
          </div>
        )}
      </Card>

      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this proposal?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the proposal from this account and is recorded in its
              activity history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
              onClick={() => {
                if (pendingDeleteId) del.mutate({proposalId: pendingDeleteId});
                setPendingDeleteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
